/**
 * Sidebar menu translations — Phase 18h.
 *
 * Separate file so the main ``translations.js`` stays readable. Key
 * pattern: ``sidebar.<slug>`` where slug is the English label
 * lower-cased, stripped of punctuation, spaces → underscores. See
 * :func:`sidebarKey` below for the canonical transform.
 *
 * Coverage strategy: we translate the common top-level parents,
 * section headings, and the most-visited children. Rarely-seen
 * admin-only labels (TDS Tracking, Bank Recon, etc.) fall back
 * automatically to English via the context's EN fallback — a
 * half-translated menu is still fully navigable.
 */

// Utility: English label → canonical key segment.
// Every non-alphanumeric character becomes a space; we then collapse
// runs of whitespace into a single underscore. That handles hyphens,
// ampersands, slashes, parentheses — everything the menu throws at us
// in one pass, without surprise empty segments.
//   "Batch / Lot"        → "sidebar.batch_lot"
//   "Auto-Debit (eNACH)" → "sidebar.auto_debit_enach"
//   "GSTR-3B Summary"    → "sidebar.gstr_3b_summary"
//   "Reviews & Trust"    → "sidebar.reviews_trust"
export const sidebarKey = (label) => {
  if (!label) return '';
  return 'sidebar.' + String(label)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, '_');
};

// Flat per-language overlays. Merged into the main translation
// dictionaries at import time (see translations.js).
//
// NOTE: keep keys alphabetised within each block so diffs stay
// readable when we add new menu items.
const overlays = {
  EN: {
    // Headings
    'sidebar.main': 'Main',
    'sidebar.my_business': 'My Business',
    'sidebar.management': 'Management',
    'sidebar.operations': 'Operations',
    'sidebar.e_commerce': 'E-Commerce',
    'sidebar.compliance': 'Compliance',
    'sidebar.finance': 'Finance',
    'sidebar.finance_settlements': 'Finance & Settlements',
    'sidebar.communication': 'Communication',
    'sidebar.administration': 'Administration',
    'sidebar.analytics': 'Analytics',
    'sidebar.integrations': 'Integrations',
    'sidebar.system': 'System',
    // Top-level
    'sidebar.dashboard': 'Dashboard',
    'sidebar.my_storage': 'My Storage',
    'sidebar.my_organization': 'My Organization',
    'sidebar.my_team': 'My Team',
    'sidebar.my_warehouses': 'My Warehouses',
    'sidebar.my_billing': 'My Billing',
    'sidebar.invoice_settings': 'Invoice Settings',
    'sidebar.warehouses': 'Warehouses',
    'sidebar.marketplace': 'Marketplace',
    'sidebar.bookings': 'Bookings',
    'sidebar.contracts': 'Contracts',
    'sidebar.inventory': 'Inventory',
    'sidebar.procurement': 'Procurement',
    'sidebar.space_mgmt': 'Space Mgmt',
    'sidebar.billing': 'Billing',
    'sidebar.settlements': 'Settlements',
    'sidebar.notifications': 'Notifications',
    'sidebar.reviews_trust': 'Reviews & Trust',
    'sidebar.disputes': 'Disputes',
    'sidebar.logistics': 'Logistics',
    'sidebar.customers': 'Customers',
    'sidebar.gst_compliance': 'GST & Compliance',
    'sidebar.my_settings': 'My Settings',
    // Warehouses children
    'sidebar.all_listings': 'All Listings',
    'sidebar.onboard_new': 'Onboard New',
    'sidebar.configure_layout': 'Configure Layout',
    'sidebar.listing_approvals': 'Listing Approvals',
    'sidebar.map_view': 'Map View',
    // Marketplace
    'sidebar.search': 'Search',
    'sidebar.saved_watchlist': 'Saved & Watchlist',
    // Bookings / Contracts
    'sidebar.all_bookings': 'All Bookings',
    'sidebar.booking_approvals': 'Booking Approvals',
    'sidebar.all_contracts': 'All Contracts',
    // Inventory
    'sidebar.warehouse_layout': 'Warehouse Layout',
    'sidebar.stock': 'Stock',
    'sidebar.grn_inbound': 'GRN (Inbound)',
    'sidebar.putaway_workbench': 'Putaway Workbench',
    'sidebar.dispatch_outbound': 'Dispatch (Outbound)',
    'sidebar.returns': 'Returns',
    'sidebar.damage_register': 'Damage Register',
    'sidebar.damage_approvals': 'Damage Approvals',
    'sidebar.cycle_count': 'Cycle Count',
    'sidebar.stock_alerts': 'Stock Alerts',
    'sidebar.barcode_qr': 'Barcode / QR',
    'sidebar.warehouse_transfer': 'Warehouse Transfer',
    'sidebar.batch_lot': 'Batch / Lot',
    'sidebar.kits_bundles': 'Kits & Bundles',
    // Procurement
    'sidebar.products_sku_master': 'Products (SKU master)',
    // Space Mgmt
    'sidebar.overview': 'Overview',
    'sidebar.zones': 'Zones',
    'sidebar.racks_bins': 'Racks & Bins',
    'sidebar.allocations': 'Allocations',
    // Operations
    'sidebar.task_board': 'Task Board',
    'sidebar.dock_schedule': 'Dock Schedule',
    'sidebar.overstay_mgmt': 'Overstay Mgmt',
    'sidebar.inbound_outbound': 'Inbound/Outbound',
    'sidebar.packaging': 'Packaging',
    'sidebar.workforce': 'Workforce',
    // E-Commerce
    'sidebar.connectors': 'Connectors',
    'sidebar.order_sync': 'Order Sync',
    'sidebar.fulfillment_center': 'Fulfillment Center',
    // Compliance
    'sidebar.hsn_sac_codes': 'HSN / SAC Codes',
    'sidebar.gst_filing': 'GST Filing',
    // Billing
    'sidebar.invoices': 'Invoices',
    'sidebar.payments': 'Payments',
    'sidebar.rate_cards': 'Rate Cards',
    'sidebar.payouts': 'Payouts',
    'sidebar.host_settlements': 'Host Settlements',
    'sidebar.my_bank_accounts': 'My Bank Accounts',
    'sidebar.reversals_claw_back': 'Reversals (claw-back)',
    'sidebar.auto_debit_enach': 'Auto-Debit (eNACH)',
    'sidebar.compliance_calendar': 'Compliance Calendar',
    'sidebar.gstr_1_report': 'GSTR-1 Report',
    'sidebar.gstr_3b_summary': 'GSTR-3B Summary',
    'sidebar.gstr_9_annual': 'GSTR-9 Annual',
    'sidebar.vendors': 'Vendors',
    'sidebar.purchase_orders': 'Purchase Orders',
    'sidebar.purchase_invoices': 'Purchase Invoices',
    'sidebar.gstr_2b_reconciliation': 'GSTR-2B Reconciliation',
    'sidebar.tax_ledger': 'Tax Ledger',
    'sidebar.e_way_bills': 'E-way Bills',
    'sidebar.credit_limits': 'Credit Limits',
    // Settlements
    'sidebar.receivables_ar': 'Receivables (AR)',
    'sidebar.payables_ap': 'Payables (AP)',
    'sidebar.settlement_engine': 'Settlement Engine',
    'sidebar.settlement_statement': 'Settlement Statement',
    'sidebar.settlement_settings': 'Settlement Settings',
    'sidebar.clawbacks': 'Clawbacks',
    'sidebar.rolling_reserve': 'Rolling Reserve',
    'sidebar.gstr_8_tcs': 'GSTR-8 (TCS)',
    'sidebar.escrow': 'Escrow',
    'sidebar.ledger': 'Ledger',
    'sidebar.bank_recon': 'Bank Recon',
    'sidebar.tds_tracking': 'TDS Tracking',
    // Notifications children
    'sidebar.inbox': 'Inbox',
    'sidebar.preferences': 'Preferences',
    'sidebar.templates': 'Templates',
    // Reviews & Trust / Disputes
    'sidebar.all_reviews': 'All Reviews',
    'sidebar.trust_badges': 'Trust & Badges',
    'sidebar.all_disputes': 'All Disputes',
    'sidebar.sla_board': 'SLA Board',
    // Logistics
    'sidebar.shipment_lifecycle': 'Shipment Lifecycle',
    'sidebar.partner_onboarding': 'Partner Onboarding',
    'sidebar.partner_management': 'Partner Management',
    'sidebar.freight_requests': 'Freight Requests',
    'sidebar.shipments': 'Shipments',
    'sidebar.earnings': 'Earnings',
    // Customers / Analytics
    'sidebar.all_customers': 'All Customers',
    'sidebar.business_intelligence': 'Business Intelligence',
    'sidebar.financial': 'Financial',
    'sidebar.platform_health': 'Platform Health',
    'sidebar.abc_xyz_analysis': 'ABC/XYZ Analysis',
    'sidebar.reports_builder': 'Reports Builder',
    // Integrations / Settings
    'sidebar.ondc_network': 'ONDC Network',
    'sidebar.api_gateway': 'API Gateway',
    'sidebar.settings': 'Settings',
    'sidebar.master_data': 'Master Data',
    'sidebar.users_roles': 'Users & Roles',
    'sidebar.kyc_queue': 'KYC Queue',
    'sidebar.moderation': 'Moderation',
    'sidebar.pricing_governance': 'Pricing Governance',
    'sidebar.platform_config': 'Platform Config',
    'sidebar.branding': 'Branding',
    'sidebar.business_rules': 'Business Rules',
    'sidebar.doc_templates': 'Doc Templates',
    'sidebar.contract_clauses': 'Contract Clauses',
    'sidebar.contract_defaults': 'Contract Defaults',
    'sidebar.contract_templates': 'Contract Templates',
    'sidebar.e_way_bill_templates': 'E-way Bill Templates',
    'sidebar.template_review_queue': 'Template Review Queue',
    'sidebar.team_members': 'Team Members',
    'sidebar.landing_page': 'Landing Page',
    'sidebar.listing_fields': 'Listing Fields',
    'sidebar.dpdp_compliance': 'DPDP Compliance',
    'sidebar.help_faq': 'Help & FAQ',
    'sidebar.audit_logs': 'Audit Logs',
    'sidebar.sla_management': 'SLA Management',
    'sidebar.feature_flags': 'Feature Flags',
    'sidebar.roles_permissions': 'Roles & Permissions',
    'sidebar.webhooks_apis': 'Webhooks & APIs',
    'sidebar.notifications_mgmt': 'Notifications Mgmt',
    'sidebar.workflow_engine': 'Workflow Engine',
    'sidebar.compliance_tools': 'Compliance Tools',
    'sidebar.scheduled_reports': 'Scheduled Reports',
    'sidebar.anomaly_alerts': 'Anomaly Alerts',
    'sidebar.support_tickets': 'Support Tickets',
    'sidebar.data_import_export': 'Data Import/Export',
    'sidebar.sms_whatsapp': 'SMS & WhatsApp',
    'sidebar.payment_gateway': 'Payment Gateway',
    'sidebar.api_documentation': 'API Documentation',
    'sidebar.changelog': 'Changelog',
    // My Settings group
    'sidebar.my_profile': 'My Profile',
    'sidebar.theme_appearance': 'Theme & Appearance',
    'sidebar.help_center': 'Help Center',
  },

  // ----- Hindi -----
  HI: {
    'sidebar.main': 'मुख्य',
    'sidebar.my_business': 'मेरा व्यवसाय',
    'sidebar.management': 'प्रबंधन',
    'sidebar.operations': 'संचालन',
    'sidebar.e_commerce': 'ई-कॉमर्स',
    'sidebar.compliance': 'अनुपालन',
    'sidebar.finance': 'वित्त',
    'sidebar.finance_settlements': 'वित्त और निपटान',
    'sidebar.communication': 'संचार',
    'sidebar.administration': 'प्रशासन',
    'sidebar.analytics': 'विश्लेषण',
    'sidebar.integrations': 'एकीकरण',
    'sidebar.system': 'प्रणाली',
    'sidebar.dashboard': 'डैशबोर्ड',
    'sidebar.my_storage': 'मेरा भंडार',
    'sidebar.my_organization': 'मेरा संगठन',
    'sidebar.my_team': 'मेरी टीम',
    'sidebar.my_warehouses': 'मेरे गोदाम',
    'sidebar.my_billing': 'मेरी बिलिंग',
    'sidebar.invoice_settings': 'चालान सेटिंग्स',
    'sidebar.warehouses': 'गोदाम',
    'sidebar.marketplace': 'बाज़ार',
    'sidebar.bookings': 'बुकिंग',
    'sidebar.contracts': 'अनुबंध',
    'sidebar.inventory': 'इन्वेंटरी',
    'sidebar.procurement': 'खरीद',
    'sidebar.space_mgmt': 'स्थान प्रबंधन',
    'sidebar.billing': 'बिलिंग',
    'sidebar.settlements': 'निपटान',
    'sidebar.notifications': 'सूचनाएँ',
    'sidebar.reviews_trust': 'समीक्षा और विश्वास',
    'sidebar.disputes': 'विवाद',
    'sidebar.logistics': 'लॉजिस्टिक्स',
    'sidebar.customers': 'ग्राहक',
    'sidebar.gst_compliance': 'GST और अनुपालन',
    'sidebar.my_settings': 'मेरी सेटिंग्स',
    'sidebar.all_listings': 'सभी लिस्टिंग',
    'sidebar.onboard_new': 'नया जोड़ें',
    'sidebar.configure_layout': 'लेआउट कॉन्फ़िगर करें',
    'sidebar.listing_approvals': 'लिस्टिंग अनुमोदन',
    'sidebar.map_view': 'मानचित्र दृश्य',
    'sidebar.search': 'खोज',
    'sidebar.saved_watchlist': 'सहेजे गए और वॉचलिस्ट',
    'sidebar.all_bookings': 'सभी बुकिंग',
    'sidebar.booking_approvals': 'बुकिंग अनुमोदन',
    'sidebar.all_contracts': 'सभी अनुबंध',
    'sidebar.warehouse_layout': 'गोदाम लेआउट',
    'sidebar.stock': 'स्टॉक',
    'sidebar.grn_inbound': 'GRN (आवक)',
    'sidebar.putaway_workbench': 'पुटअवे वर्कबेंच',
    'sidebar.dispatch_outbound': 'प्रेषण (जावक)',
    'sidebar.returns': 'वापसी',
    'sidebar.damage_register': 'क्षति रजिस्टर',
    'sidebar.damage_approvals': 'क्षति अनुमोदन',
    'sidebar.cycle_count': 'चक्र गणना',
    'sidebar.stock_alerts': 'स्टॉक अलर्ट',
    'sidebar.barcode_qr': 'बारकोड / QR',
    'sidebar.warehouse_transfer': 'गोदाम स्थानांतरण',
    'sidebar.batch_lot': 'बैच / लॉट',
    'sidebar.kits_bundles': 'किट और बंडल',
    'sidebar.products_sku_master': 'उत्पाद (SKU मास्टर)',
    'sidebar.overview': 'अवलोकन',
    'sidebar.zones': 'ज़ोन',
    'sidebar.racks_bins': 'रैक और बिन',
    'sidebar.allocations': 'आवंटन',
    'sidebar.task_board': 'कार्य बोर्ड',
    'sidebar.dock_schedule': 'डॉक अनुसूची',
    'sidebar.overstay_mgmt': 'अतिवास प्रबंधन',
    'sidebar.inbound_outbound': 'आवक/जावक',
    'sidebar.packaging': 'पैकेजिंग',
    'sidebar.workforce': 'कार्यबल',
    'sidebar.connectors': 'कनेक्टर',
    'sidebar.order_sync': 'ऑर्डर सिंक',
    'sidebar.fulfillment_center': 'पूर्ति केंद्र',
    'sidebar.hsn_sac_codes': 'HSN / SAC कोड',
    'sidebar.gst_filing': 'GST दाखिल',
    'sidebar.invoices': 'चालान',
    'sidebar.payments': 'भुगतान',
    'sidebar.rate_cards': 'दर कार्ड',
    'sidebar.payouts': 'पेआउट',
    'sidebar.host_settlements': 'होस्ट निपटान',
    'sidebar.my_bank_accounts': 'मेरे बैंक खाते',
    'sidebar.reversals_claw_back': 'उलटाव (क्लॉ-बैक)',
    'sidebar.auto_debit_enach': 'स्वतः-डेबिट (eNACH)',
    'sidebar.compliance_calendar': 'अनुपालन कैलेंडर',
    'sidebar.gstr_1_report': 'GSTR-1 रिपोर्ट',
    'sidebar.gstr_3b_summary': 'GSTR-3B सारांश',
    'sidebar.gstr_9_annual': 'GSTR-9 वार्षिक',
    'sidebar.vendors': 'विक्रेता',
    'sidebar.purchase_orders': 'खरीद आदेश',
    'sidebar.purchase_invoices': 'खरीद चालान',
    'sidebar.gstr_2b_reconciliation': 'GSTR-2B सुलह',
    'sidebar.tax_ledger': 'कर बहीखाता',
    'sidebar.e_way_bills': 'ई-वे बिल',
    'sidebar.credit_limits': 'क्रेडिट सीमा',
    'sidebar.receivables_ar': 'प्राप्य (AR)',
    'sidebar.payables_ap': 'देय (AP)',
    'sidebar.settlement_engine': 'निपटान इंजन',
    'sidebar.settlement_statement': 'निपटान विवरण',
    'sidebar.settlement_settings': 'निपटान सेटिंग्स',
    'sidebar.clawbacks': 'क्लॉबैक',
    'sidebar.rolling_reserve': 'रोलिंग रिज़र्व',
    'sidebar.gstr_8_tcs': 'GSTR-8 (TCS)',
    'sidebar.escrow': 'एस्क्रो',
    'sidebar.ledger': 'बहीखाता',
    'sidebar.bank_recon': 'बैंक सुलह',
    'sidebar.tds_tracking': 'TDS ट्रैकिंग',
    'sidebar.inbox': 'इनबॉक्स',
    'sidebar.preferences': 'प्राथमिकताएँ',
    'sidebar.templates': 'टेम्पलेट',
    'sidebar.all_reviews': 'सभी समीक्षाएँ',
    'sidebar.trust_badges': 'विश्वास और बैज',
    'sidebar.all_disputes': 'सभी विवाद',
    'sidebar.sla_board': 'SLA बोर्ड',
    'sidebar.shipment_lifecycle': 'शिपमेंट जीवनचक्र',
    'sidebar.partner_onboarding': 'पार्टनर ऑनबोर्डिंग',
    'sidebar.partner_management': 'पार्टनर प्रबंधन',
    'sidebar.freight_requests': 'माल अनुरोध',
    'sidebar.shipments': 'शिपमेंट',
    'sidebar.earnings': 'कमाई',
    'sidebar.all_customers': 'सभी ग्राहक',
    'sidebar.business_intelligence': 'बिज़नेस इंटेलिजेंस',
    'sidebar.financial': 'वित्तीय',
    'sidebar.platform_health': 'प्लेटफ़ॉर्म स्वास्थ्य',
    'sidebar.abc_xyz_analysis': 'ABC/XYZ विश्लेषण',
    'sidebar.reports_builder': 'रिपोर्ट बिल्डर',
    'sidebar.ondc_network': 'ONDC नेटवर्क',
    'sidebar.api_gateway': 'API गेटवे',
    'sidebar.settings': 'सेटिंग्स',
    'sidebar.master_data': 'मास्टर डेटा',
    'sidebar.users_roles': 'उपयोगकर्ता और भूमिकाएँ',
    'sidebar.kyc_queue': 'KYC कतार',
    'sidebar.moderation': 'मॉडरेशन',
    'sidebar.pricing_governance': 'मूल्य निर्धारण प्रशासन',
    'sidebar.platform_config': 'प्लेटफ़ॉर्म कॉन्फ़िग',
    'sidebar.branding': 'ब्रांडिंग',
    'sidebar.business_rules': 'व्यावसायिक नियम',
    'sidebar.doc_templates': 'दस्तावेज़ टेम्पलेट',
    'sidebar.contract_clauses': 'अनुबंध खंड',
    'sidebar.contract_defaults': 'अनुबंध डिफ़ॉल्ट',
    'sidebar.contract_templates': 'अनुबंध टेम्पलेट',
    'sidebar.e_way_bill_templates': 'ई-वे बिल टेम्पलेट',
    'sidebar.template_review_queue': 'टेम्पलेट समीक्षा कतार',
    'sidebar.team_members': 'टीम सदस्य',
    'sidebar.landing_page': 'लैंडिंग पृष्ठ',
    'sidebar.listing_fields': 'लिस्टिंग फ़ील्ड',
    'sidebar.dpdp_compliance': 'DPDP अनुपालन',
    'sidebar.help_faq': 'सहायता और FAQ',
    'sidebar.audit_logs': 'ऑडिट लॉग',
    'sidebar.sla_management': 'SLA प्रबंधन',
    'sidebar.feature_flags': 'फ़ीचर फ़्लैग',
    'sidebar.roles_permissions': 'भूमिकाएँ और अनुमतियाँ',
    'sidebar.webhooks_apis': 'वेबहुक और API',
    'sidebar.notifications_mgmt': 'सूचना प्रबंधन',
    'sidebar.workflow_engine': 'वर्कफ़्लो इंजन',
    'sidebar.compliance_tools': 'अनुपालन उपकरण',
    'sidebar.scheduled_reports': 'अनुसूचित रिपोर्ट',
    'sidebar.anomaly_alerts': 'विसंगति अलर्ट',
    'sidebar.support_tickets': 'समर्थन टिकट',
    'sidebar.data_import_export': 'डेटा आयात/निर्यात',
    'sidebar.sms_whatsapp': 'SMS और WhatsApp',
    'sidebar.payment_gateway': 'भुगतान गेटवे',
    'sidebar.api_documentation': 'API दस्तावेज़ीकरण',
    'sidebar.changelog': 'परिवर्तन लॉग',
    'sidebar.my_profile': 'मेरी प्रोफ़ाइल',
    'sidebar.theme_appearance': 'थीम और रूप',
    'sidebar.help_center': 'सहायता केंद्र',
  },

  // ----- Tamil -----
  TA: {
    'sidebar.main': 'முதன்மை',
    'sidebar.my_business': 'என் வணிகம்',
    'sidebar.management': 'மேலாண்மை',
    'sidebar.operations': 'செயல்பாடுகள்',
    'sidebar.e_commerce': 'மின் வணிகம்',
    'sidebar.compliance': 'இணக்கம்',
    'sidebar.finance': 'நிதி',
    'sidebar.finance_settlements': 'நிதி மற்றும் தீர்வு',
    'sidebar.communication': 'தொடர்பு',
    'sidebar.administration': 'நிர்வாகம்',
    'sidebar.analytics': 'பகுப்பாய்வு',
    'sidebar.integrations': 'ஒருங்கிணைப்புகள்',
    'sidebar.system': 'முறைமை',
    'sidebar.dashboard': 'டாஷ்போர்டு',
    'sidebar.my_storage': 'என் சேமிப்பு',
    'sidebar.my_organization': 'என் நிறுவனம்',
    'sidebar.my_team': 'என் குழு',
    'sidebar.my_warehouses': 'என் கிடங்குகள்',
    'sidebar.my_billing': 'என் பில்லிங்',
    'sidebar.invoice_settings': 'விலைப்பட்டியல் அமைப்புகள்',
    'sidebar.warehouses': 'கிடங்குகள்',
    'sidebar.marketplace': 'சந்தை',
    'sidebar.bookings': 'முன்பதிவுகள்',
    'sidebar.contracts': 'ஒப்பந்தங்கள்',
    'sidebar.inventory': 'இருப்பு',
    'sidebar.procurement': 'கொள்முதல்',
    'sidebar.space_mgmt': 'இட மேலாண்மை',
    'sidebar.billing': 'பில்லிங்',
    'sidebar.settlements': 'தீர்வுகள்',
    'sidebar.notifications': 'அறிவிப்புகள்',
    'sidebar.reviews_trust': 'மதிப்புரைகள் & நம்பிக்கை',
    'sidebar.disputes': 'தகராறுகள்',
    'sidebar.logistics': 'தளவாடம்',
    'sidebar.customers': 'வாடிக்கையாளர்கள்',
    'sidebar.gst_compliance': 'GST & இணக்கம்',
    'sidebar.my_settings': 'என் அமைப்புகள்',
    'sidebar.all_listings': 'அனைத்து பட்டியல்கள்',
    'sidebar.onboard_new': 'புதியதை சேர்',
    'sidebar.configure_layout': 'தளவமைப்பு கட்டமை',
    'sidebar.listing_approvals': 'பட்டியல் ஒப்புதல்கள்',
    'sidebar.map_view': 'வரைபட காட்சி',
    'sidebar.search': 'தேடல்',
    'sidebar.saved_watchlist': 'சேமித்தவை & கவனிப்பு',
    'sidebar.all_bookings': 'அனைத்து முன்பதிவுகள்',
    'sidebar.booking_approvals': 'முன்பதிவு ஒப்புதல்கள்',
    'sidebar.all_contracts': 'அனைத்து ஒப்பந்தங்கள்',
    'sidebar.warehouse_layout': 'கிடங்கு தளவமைப்பு',
    'sidebar.stock': 'சரக்கு',
    'sidebar.grn_inbound': 'GRN (உள் வரத்து)',
    'sidebar.putaway_workbench': 'புட்அவே பணிமேடை',
    'sidebar.dispatch_outbound': 'அனுப்புதல் (வெளி)',
    'sidebar.returns': 'திரும்பப் பெறுதல்',
    'sidebar.damage_register': 'சேத பதிவேடு',
    'sidebar.damage_approvals': 'சேத ஒப்புதல்கள்',
    'sidebar.cycle_count': 'சுழற்சி எண்ணிக்கை',
    'sidebar.stock_alerts': 'சரக்கு எச்சரிக்கைகள்',
    'sidebar.barcode_qr': 'பார்கோடு / QR',
    'sidebar.warehouse_transfer': 'கிடங்கு மாற்றம்',
    'sidebar.batch_lot': 'பேட்ச் / லாட்',
    'sidebar.kits_bundles': 'கிட்டுகள் & தொகுப்புகள்',
    'sidebar.products_sku_master': 'பொருட்கள் (SKU முதன்மை)',
    'sidebar.overview': 'மேலோட்டம்',
    'sidebar.zones': 'மண்டலங்கள்',
    'sidebar.racks_bins': 'அடுக்குகள் & தொட்டிகள்',
    'sidebar.allocations': 'ஒதுக்கீடுகள்',
    'sidebar.task_board': 'பணி பலகை',
    'sidebar.dock_schedule': 'டாக் அட்டவணை',
    'sidebar.overstay_mgmt': 'தங்கல் மேலாண்மை',
    'sidebar.inbound_outbound': 'உள்வரவு/வெளிச்செல்',
    'sidebar.packaging': 'தொகுப்பு',
    'sidebar.workforce': 'பணியாளர் படை',
    'sidebar.connectors': 'இணைப்பிகள்',
    'sidebar.order_sync': 'ஆர்டர் ஒத்திசைவு',
    'sidebar.fulfillment_center': 'நிறைவேற்ற மையம்',
    'sidebar.hsn_sac_codes': 'HSN / SAC குறியீடுகள்',
    'sidebar.gst_filing': 'GST தாக்கல்',
    'sidebar.invoices': 'விலைப்பட்டியல்கள்',
    'sidebar.payments': 'கட்டணங்கள்',
    'sidebar.rate_cards': 'விலை அட்டைகள்',
    'sidebar.payouts': 'பணம் செலுத்துதல்கள்',
    'sidebar.host_settlements': 'ஹோஸ்ட் தீர்வுகள்',
    'sidebar.my_bank_accounts': 'என் வங்கி கணக்குகள்',
    'sidebar.reversals_claw_back': 'மாற்றல்கள் (கிளா-பேக்)',
    'sidebar.auto_debit_enach': 'தானியங்கி-டெபிட் (eNACH)',
    'sidebar.compliance_calendar': 'இணக்க நாட்காட்டி',
    'sidebar.gstr_1_report': 'GSTR-1 அறிக்கை',
    'sidebar.gstr_3b_summary': 'GSTR-3B சுருக்கம்',
    'sidebar.gstr_9_annual': 'GSTR-9 ஆண்டு',
    'sidebar.vendors': 'விற்பனையாளர்கள்',
    'sidebar.purchase_orders': 'கொள்முதல் ஆர்டர்கள்',
    'sidebar.purchase_invoices': 'கொள்முதல் பில்கள்',
    'sidebar.gstr_2b_reconciliation': 'GSTR-2B சரிகட்டல்',
    'sidebar.tax_ledger': 'வரி பேரேடு',
    'sidebar.e_way_bills': 'ஈ-வே பில்கள்',
    'sidebar.credit_limits': 'கடன் வரம்புகள்',
    'sidebar.receivables_ar': 'பெறத்தக்கவை (AR)',
    'sidebar.payables_ap': 'செலுத்தத்தக்கவை (AP)',
    'sidebar.settlement_engine': 'தீர்வு இயந்திரம்',
    'sidebar.settlement_statement': 'தீர்வு அறிக்கை',
    'sidebar.settlement_settings': 'தீர்வு அமைப்புகள்',
    'sidebar.clawbacks': 'கிளாபேக்குகள்',
    'sidebar.rolling_reserve': 'சுழல் இருப்பு',
    'sidebar.gstr_8_tcs': 'GSTR-8 (TCS)',
    'sidebar.escrow': 'எஸ்க்ரோ',
    'sidebar.ledger': 'பேரேடு',
    'sidebar.bank_recon': 'வங்கி சரிகட்டல்',
    'sidebar.tds_tracking': 'TDS கண்காணிப்பு',
    'sidebar.inbox': 'உள்பெட்டி',
    'sidebar.preferences': 'விருப்பங்கள்',
    'sidebar.templates': 'டெம்ப்ளேட்டுகள்',
    'sidebar.all_reviews': 'அனைத்து மதிப்புரைகள்',
    'sidebar.trust_badges': 'நம்பிக்கை & பேட்ஜ்கள்',
    'sidebar.all_disputes': 'அனைத்து தகராறுகள்',
    'sidebar.sla_board': 'SLA பலகை',
    'sidebar.shipment_lifecycle': 'ஏற்றுமதி வாழ்க்கை',
    'sidebar.partner_onboarding': 'பங்குதாரர் சேர்ப்பு',
    'sidebar.partner_management': 'பங்குதாரர் மேலாண்மை',
    'sidebar.freight_requests': 'சரக்கு கோரிக்கைகள்',
    'sidebar.shipments': 'ஏற்றுமதிகள்',
    'sidebar.earnings': 'வருவாய்',
    'sidebar.all_customers': 'அனைத்து வாடிக்கையாளர்கள்',
    'sidebar.business_intelligence': 'வணிக நுண்ணறிவு',
    'sidebar.financial': 'நிதி',
    'sidebar.platform_health': 'தளம் ஆரோக்கியம்',
    'sidebar.abc_xyz_analysis': 'ABC/XYZ பகுப்பாய்வு',
    'sidebar.reports_builder': 'அறிக்கை உருவாக்கி',
    'sidebar.ondc_network': 'ONDC நெட்வொர்க்',
    'sidebar.api_gateway': 'API நுழைவாயில்',
    'sidebar.settings': 'அமைப்புகள்',
    'sidebar.master_data': 'முதன்மை தரவு',
    'sidebar.users_roles': 'பயனர்கள் & பாத்திரங்கள்',
    'sidebar.kyc_queue': 'KYC வரிசை',
    'sidebar.moderation': 'மிதப்படுத்தல்',
    'sidebar.pricing_governance': 'விலை நிர்வாகம்',
    'sidebar.platform_config': 'தள கட்டமைப்பு',
    'sidebar.branding': 'பிராண்டிங்',
    'sidebar.business_rules': 'வணிக விதிகள்',
    'sidebar.doc_templates': 'ஆவண டெம்ப்ளேட்கள்',
    'sidebar.contract_clauses': 'ஒப்பந்த பிரிவுகள்',
    'sidebar.contract_defaults': 'ஒப்பந்த இயல்புகள்',
    'sidebar.contract_templates': 'ஒப்பந்த டெம்ப்ளேட்கள்',
    'sidebar.e_way_bill_templates': 'ஈ-வே பில் டெம்ப்ளேட்',
    'sidebar.template_review_queue': 'டெம்ப்ளேட் மதிப்பாய்வு',
    'sidebar.team_members': 'குழு உறுப்பினர்கள்',
    'sidebar.landing_page': 'தரையிறக்க பக்கம்',
    'sidebar.listing_fields': 'பட்டியல் புலங்கள்',
    'sidebar.dpdp_compliance': 'DPDP இணக்கம்',
    'sidebar.help_faq': 'உதவி & FAQ',
    'sidebar.audit_logs': 'தணிக்கை பதிவுகள்',
    'sidebar.sla_management': 'SLA மேலாண்மை',
    'sidebar.feature_flags': 'அம்ச கொடிகள்',
    'sidebar.roles_permissions': 'பாத்திரங்கள் & அனுமதிகள்',
    'sidebar.webhooks_apis': 'வெப்ஹூக்ஸ் & API',
    'sidebar.notifications_mgmt': 'அறிவிப்பு மேலாண்மை',
    'sidebar.workflow_engine': 'பணிப்பாய்வு இயந்திரம்',
    'sidebar.compliance_tools': 'இணக்க கருவிகள்',
    'sidebar.scheduled_reports': 'திட்டமிட்ட அறிக்கைகள்',
    'sidebar.anomaly_alerts': 'விசேட எச்சரிக்கைகள்',
    'sidebar.support_tickets': 'ஆதரவு டிக்கெட்கள்',
    'sidebar.data_import_export': 'தரவு இறக்கம்/ஏற்றம்',
    'sidebar.sms_whatsapp': 'SMS & WhatsApp',
    'sidebar.payment_gateway': 'கட்டண நுழைவாயில்',
    'sidebar.api_documentation': 'API ஆவணப்படுத்தல்',
    'sidebar.changelog': 'மாற்றப் பதிவு',
    'sidebar.my_profile': 'என் சுயவிவரம்',
    'sidebar.theme_appearance': 'தீம் & தோற்றம்',
    'sidebar.help_center': 'உதவி மையம்',
  },

  // ----- Telugu -----
  TE: {
    'sidebar.main': 'ప్రధాన',
    'sidebar.my_business': 'నా వ్యాపారం',
    'sidebar.management': 'నిర్వహణ',
    'sidebar.operations': 'కార్యకలాపాలు',
    'sidebar.e_commerce': 'ఇ-కామర్స్',
    'sidebar.compliance': 'అనుసరణ',
    'sidebar.finance': 'ఆర్థికం',
    'sidebar.finance_settlements': 'ఆర్థికం & పరిష్కారాలు',
    'sidebar.communication': 'సంభాషణ',
    'sidebar.administration': 'నిర్వాహణ',
    'sidebar.analytics': 'విశ్లేషణలు',
    'sidebar.integrations': 'సమైక్యతలు',
    'sidebar.system': 'వ్యవస్థ',
    'sidebar.dashboard': 'డ్యాష్‌బోర్డ్',
    'sidebar.my_storage': 'నా నిల్వ',
    'sidebar.my_organization': 'నా సంస్థ',
    'sidebar.my_team': 'నా బృందం',
    'sidebar.my_warehouses': 'నా గిడ్డంగులు',
    'sidebar.my_billing': 'నా బిల్లింగ్',
    'sidebar.invoice_settings': 'ఇన్వాయిస్ సెట్టింగ్‌లు',
    'sidebar.warehouses': 'గిడ్డంగులు',
    'sidebar.marketplace': 'మార్కెట్‌ప్లేస్',
    'sidebar.bookings': 'బుకింగ్‌లు',
    'sidebar.contracts': 'ఒప్పందాలు',
    'sidebar.inventory': 'ఇన్వెంటరీ',
    'sidebar.procurement': 'కొనుగోలు',
    'sidebar.space_mgmt': 'స్థల నిర్వహణ',
    'sidebar.billing': 'బిల్లింగ్',
    'sidebar.settlements': 'పరిష్కారాలు',
    'sidebar.notifications': 'నోటిఫికేషన్‌లు',
    'sidebar.reviews_trust': 'సమీక్షలు & విశ్వాసం',
    'sidebar.disputes': 'వివాదాలు',
    'sidebar.logistics': 'లాజిస్టిక్స్',
    'sidebar.customers': 'వినియోగదారులు',
    'sidebar.gst_compliance': 'GST & అనుసరణ',
    'sidebar.my_settings': 'నా సెట్టింగ్‌లు',
    'sidebar.all_listings': 'అన్ని జాబితాలు',
    'sidebar.onboard_new': 'కొత్తది జోడించండి',
    'sidebar.configure_layout': 'లేఅవుట్ కాన్ఫిగర్',
    'sidebar.listing_approvals': 'జాబితా ఆమోదాలు',
    'sidebar.map_view': 'మ్యాప్ వీక్షణ',
    'sidebar.search': 'వెతుకు',
    'sidebar.saved_watchlist': 'సేవ్ చేసినవి & వాచ్‌లిస్ట్',
    'sidebar.all_bookings': 'అన్ని బుకింగ్‌లు',
    'sidebar.booking_approvals': 'బుకింగ్ ఆమోదాలు',
    'sidebar.all_contracts': 'అన్ని ఒప్పందాలు',
    'sidebar.warehouse_layout': 'గిడ్డంగి లేఅవుట్',
    'sidebar.stock': 'స్టాక్',
    'sidebar.grn_inbound': 'GRN (రాక)',
    'sidebar.putaway_workbench': 'పుటవే వర్క్‌బెంచ్',
    'sidebar.dispatch_outbound': 'పంపకం (వెళ్లే)',
    'sidebar.returns': 'తిరిగి వచ్చినవి',
    'sidebar.damage_register': 'నష్ట నమోదు',
    'sidebar.damage_approvals': 'నష్ట ఆమోదాలు',
    'sidebar.cycle_count': 'చక్ర గణన',
    'sidebar.stock_alerts': 'స్టాక్ హెచ్చరికలు',
    'sidebar.barcode_qr': 'బార్‌కోడ్ / QR',
    'sidebar.warehouse_transfer': 'గిడ్డంగి బదిలీ',
    'sidebar.batch_lot': 'బ్యాచ్ / లాట్',
    'sidebar.kits_bundles': 'కిట్లు & బండిల్స్',
    'sidebar.products_sku_master': 'ఉత్పత్తులు (SKU మాస్టర్)',
    'sidebar.overview': 'అవలోకనం',
    'sidebar.zones': 'జోన్‌లు',
    'sidebar.racks_bins': 'ర్యాక్‌లు & బిన్‌లు',
    'sidebar.allocations': 'కేటాయింపులు',
    'sidebar.task_board': 'టాస్క్ బోర్డు',
    'sidebar.dock_schedule': 'డాక్ షెడ్యూల్',
    'sidebar.overstay_mgmt': 'ఓవర్‌స్టే నిర్వహణ',
    'sidebar.inbound_outbound': 'ఇన్‌బౌండ్/ఔట్‌బౌండ్',
    'sidebar.packaging': 'ప్యాకేజింగ్',
    'sidebar.workforce': 'శ్రామిక శక్తి',
    'sidebar.connectors': 'కనెక్టర్లు',
    'sidebar.order_sync': 'ఆర్డర్ సింక్',
    'sidebar.fulfillment_center': 'పూర్తి కేంద్రం',
    'sidebar.hsn_sac_codes': 'HSN / SAC కోడ్‌లు',
    'sidebar.gst_filing': 'GST దాఖలు',
    'sidebar.invoices': 'ఇన్వాయిస్‌లు',
    'sidebar.payments': 'చెల్లింపులు',
    'sidebar.rate_cards': 'రేట్ కార్డులు',
    'sidebar.payouts': 'చెల్లింపులు',
    'sidebar.host_settlements': 'హోస్ట్ పరిష్కారాలు',
    'sidebar.my_bank_accounts': 'నా బ్యాంక్ ఖాతాలు',
    'sidebar.reversals_claw_back': 'రివర్సల్స్ (క్లా-బ్యాక్)',
    'sidebar.auto_debit_enach': 'ఆటో-డెబిట్ (eNACH)',
    'sidebar.compliance_calendar': 'అనుసరణ క్యాలెండర్',
    'sidebar.gstr_1_report': 'GSTR-1 నివేదిక',
    'sidebar.gstr_3b_summary': 'GSTR-3B సారాంశం',
    'sidebar.gstr_9_annual': 'GSTR-9 వార్షిక',
    'sidebar.vendors': 'విక్రేతలు',
    'sidebar.purchase_orders': 'కొనుగోలు ఆర్డర్లు',
    'sidebar.purchase_invoices': 'కొనుగోలు ఇన్వాయిస్‌లు',
    'sidebar.gstr_2b_reconciliation': 'GSTR-2B సయోధ్య',
    'sidebar.tax_ledger': 'పన్ను లెడ్జర్',
    'sidebar.e_way_bills': 'ఇ-వే బిల్లులు',
    'sidebar.credit_limits': 'క్రెడిట్ పరిమితులు',
    'sidebar.receivables_ar': 'స్వీకార్యం (AR)',
    'sidebar.payables_ap': 'చెల్లింపులు (AP)',
    'sidebar.settlement_engine': 'పరిష్కార ఇంజిన్',
    'sidebar.settlement_statement': 'పరిష్కార ప్రకటన',
    'sidebar.settlement_settings': 'పరిష్కార సెట్టింగ్‌లు',
    'sidebar.clawbacks': 'క్లాబ్యాక్‌లు',
    'sidebar.rolling_reserve': 'రోలింగ్ రిజర్వ్',
    'sidebar.gstr_8_tcs': 'GSTR-8 (TCS)',
    'sidebar.escrow': 'ఎస్క్రో',
    'sidebar.ledger': 'లెడ్జర్',
    'sidebar.bank_recon': 'బ్యాంక్ సయోధ్య',
    'sidebar.tds_tracking': 'TDS ట్రాకింగ్',
    'sidebar.inbox': 'ఇన్‌బాక్స్',
    'sidebar.preferences': 'ప్రాధాన్యతలు',
    'sidebar.templates': 'టెంప్లేట్లు',
    'sidebar.all_reviews': 'అన్ని సమీక్షలు',
    'sidebar.trust_badges': 'విశ్వాసం & బ్యాడ్జ్‌లు',
    'sidebar.all_disputes': 'అన్ని వివాదాలు',
    'sidebar.sla_board': 'SLA బోర్డు',
    'sidebar.shipment_lifecycle': 'షిప్‌మెంట్ జీవిత చక్రం',
    'sidebar.partner_onboarding': 'భాగస్వామి ఆన్‌బోర్డింగ్',
    'sidebar.partner_management': 'భాగస్వామి నిర్వహణ',
    'sidebar.freight_requests': 'సరుకు అభ్యర్థనలు',
    'sidebar.shipments': 'షిప్‌మెంట్లు',
    'sidebar.earnings': 'ఆదాయాలు',
    'sidebar.all_customers': 'అన్ని వినియోగదారులు',
    'sidebar.business_intelligence': 'వ్యాపార మేధస్సు',
    'sidebar.financial': 'ఆర్థిక',
    'sidebar.platform_health': 'ప్లాట్‌ఫారమ్ ఆరోగ్యం',
    'sidebar.abc_xyz_analysis': 'ABC/XYZ విశ్లేషణ',
    'sidebar.reports_builder': 'నివేదిక బిల్డర్',
    'sidebar.ondc_network': 'ONDC నెట్‌వర్క్',
    'sidebar.api_gateway': 'API గేట్‌వే',
    'sidebar.settings': 'సెట్టింగ్‌లు',
    'sidebar.master_data': 'మాస్టర్ డేటా',
    'sidebar.users_roles': 'యూజర్లు & పాత్రలు',
    'sidebar.kyc_queue': 'KYC క్యూ',
    'sidebar.moderation': 'మోడరేషన్',
    'sidebar.pricing_governance': 'ధర పాలన',
    'sidebar.platform_config': 'ప్లాట్‌ఫారమ్ కాన్ఫిగ్',
    'sidebar.branding': 'బ్రాండింగ్',
    'sidebar.business_rules': 'వ్యాపార నియమాలు',
    'sidebar.doc_templates': 'డాక్ టెంప్లేట్లు',
    'sidebar.contract_clauses': 'ఒప్పంద నిబంధనలు',
    'sidebar.contract_defaults': 'ఒప్పంద డిఫాల్ట్‌లు',
    'sidebar.contract_templates': 'ఒప్పంద టెంప్లేట్లు',
    'sidebar.e_way_bill_templates': 'ఇ-వే బిల్ టెంప్లేట్లు',
    'sidebar.template_review_queue': 'టెంప్లేట్ సమీక్ష క్యూ',
    'sidebar.team_members': 'బృంద సభ్యులు',
    'sidebar.landing_page': 'ల్యాండింగ్ పేజ్',
    'sidebar.listing_fields': 'జాబితా ఫీల్డ్‌లు',
    'sidebar.dpdp_compliance': 'DPDP అనుసరణ',
    'sidebar.help_faq': 'సహాయం & FAQ',
    'sidebar.audit_logs': 'ఆడిట్ లాగ్‌లు',
    'sidebar.sla_management': 'SLA నిర్వహణ',
    'sidebar.feature_flags': 'ఫీచర్ ఫ్లాగ్‌లు',
    'sidebar.roles_permissions': 'పాత్రలు & అనుమతులు',
    'sidebar.webhooks_apis': 'వెబ్‌హుక్స్ & API',
    'sidebar.notifications_mgmt': 'నోటిఫికేషన్ నిర్వహణ',
    'sidebar.workflow_engine': 'వర్క్‌ఫ్లో ఇంజిన్',
    'sidebar.compliance_tools': 'అనుసరణ సాధనాలు',
    'sidebar.scheduled_reports': 'షెడ్యూల్ చేసిన నివేదికలు',
    'sidebar.anomaly_alerts': 'విసంగతి హెచ్చరికలు',
    'sidebar.support_tickets': 'మద్దతు టిక్కెట్లు',
    'sidebar.data_import_export': 'డేటా దిగుమతి/ఎగుమతి',
    'sidebar.sms_whatsapp': 'SMS & WhatsApp',
    'sidebar.payment_gateway': 'చెల్లింపు గేట్‌వే',
    'sidebar.api_documentation': 'API డాక్యుమెంటేషన్',
    'sidebar.changelog': 'మార్పుల లాగ్',
    'sidebar.my_profile': 'నా ప్రొఫైల్',
    'sidebar.theme_appearance': 'థీమ్ & రూపం',
    'sidebar.help_center': 'సహాయ కేంద్రం',
  },

  // ----- Kannada -----
  KN: {
    'sidebar.main': 'ಮುಖ್ಯ',
    'sidebar.my_business': 'ನನ್ನ ವ್ಯಾಪಾರ',
    'sidebar.management': 'ನಿರ್ವಹಣೆ',
    'sidebar.operations': 'ಕಾರ್ಯಾಚರಣೆಗಳು',
    'sidebar.e_commerce': 'ಇ-ಕಾಮರ್ಸ್',
    'sidebar.compliance': 'ಅನುಸರಣೆ',
    'sidebar.finance': 'ಹಣಕಾಸು',
    'sidebar.finance_settlements': 'ಹಣಕಾಸು & ಇತ್ಯರ್ಥಗಳು',
    'sidebar.communication': 'ಸಂವಹನ',
    'sidebar.administration': 'ಆಡಳಿತ',
    'sidebar.analytics': 'ವಿಶ್ಲೇಷಣೆ',
    'sidebar.integrations': 'ಸಂಯೋಜನೆಗಳು',
    'sidebar.system': 'ವ್ಯವಸ್ಥೆ',
    'sidebar.dashboard': 'ಡ್ಯಾಶ್‌ಬೋರ್ಡ್',
    'sidebar.my_storage': 'ನನ್ನ ಸಂಗ್ರಹಣೆ',
    'sidebar.my_organization': 'ನನ್ನ ಸಂಸ್ಥೆ',
    'sidebar.my_team': 'ನನ್ನ ತಂಡ',
    'sidebar.my_warehouses': 'ನನ್ನ ಗೋದಾಮುಗಳು',
    'sidebar.my_billing': 'ನನ್ನ ಬಿಲ್ಲಿಂಗ್',
    'sidebar.invoice_settings': 'ಇನ್‌ವಾಯಿಸ್ ಸೆಟ್ಟಿಂಗ್‌ಗಳು',
    'sidebar.warehouses': 'ಗೋದಾಮುಗಳು',
    'sidebar.marketplace': 'ಮಾರುಕಟ್ಟೆ',
    'sidebar.bookings': 'ಬುಕ್ಕಿಂಗ್‌ಗಳು',
    'sidebar.contracts': 'ಒಪ್ಪಂದಗಳು',
    'sidebar.inventory': 'ಇನ್ವೆಂಟರಿ',
    'sidebar.procurement': 'ಖರೀದಿ',
    'sidebar.space_mgmt': 'ಸ್ಥಳ ನಿರ್ವಹಣೆ',
    'sidebar.billing': 'ಬಿಲ್ಲಿಂಗ್',
    'sidebar.settlements': 'ಇತ್ಯರ್ಥಗಳು',
    'sidebar.notifications': 'ಅಧಿಸೂಚನೆಗಳು',
    'sidebar.reviews_trust': 'ವಿಮರ್ಶೆಗಳು & ನಂಬಿಕೆ',
    'sidebar.disputes': 'ವಿವಾದಗಳು',
    'sidebar.logistics': 'ಲಾಜಿಸ್ಟಿಕ್ಸ್',
    'sidebar.customers': 'ಗ್ರಾಹಕರು',
    'sidebar.gst_compliance': 'GST & ಅನುಸರಣೆ',
    'sidebar.my_settings': 'ನನ್ನ ಸೆಟ್ಟಿಂಗ್‌ಗಳು',
    'sidebar.all_listings': 'ಎಲ್ಲಾ ಪಟ್ಟಿಗಳು',
    'sidebar.onboard_new': 'ಹೊಸದನ್ನು ಸೇರಿಸಿ',
    'sidebar.configure_layout': 'ಲೇಔಟ್ ಕಾನ್ಫಿಗರ್',
    'sidebar.listing_approvals': 'ಪಟ್ಟಿ ಅನುಮೋದನೆಗಳು',
    'sidebar.map_view': 'ನಕ್ಷೆ ನೋಟ',
    'sidebar.search': 'ಹುಡುಕಿ',
    'sidebar.saved_watchlist': 'ಉಳಿಸಿದ & ವೀಕ್ಷಣಾಪಟ್ಟಿ',
    'sidebar.all_bookings': 'ಎಲ್ಲಾ ಬುಕ್ಕಿಂಗ್‌ಗಳು',
    'sidebar.booking_approvals': 'ಬುಕ್ಕಿಂಗ್ ಅನುಮೋದನೆಗಳು',
    'sidebar.all_contracts': 'ಎಲ್ಲಾ ಒಪ್ಪಂದಗಳು',
    'sidebar.warehouse_layout': 'ಗೋದಾಮು ಲೇಔಟ್',
    'sidebar.stock': 'ಸ್ಟಾಕ್',
    'sidebar.grn_inbound': 'GRN (ಒಳಬರುವ)',
    'sidebar.putaway_workbench': 'ಪುಟ್‌ಅವೇ ವರ್ಕ್‌ಬೆಂಚ್',
    'sidebar.dispatch_outbound': 'ರವಾನೆ (ಹೊರಹೋಗುವ)',
    'sidebar.returns': 'ಹಿಂತಿರುಗಿಸುವಿಕೆಗಳು',
    'sidebar.damage_register': 'ಹಾನಿ ನೋಂದಣಿ',
    'sidebar.damage_approvals': 'ಹಾನಿ ಅನುಮೋದನೆಗಳು',
    'sidebar.cycle_count': 'ಚಕ್ರ ಎಣಿಕೆ',
    'sidebar.stock_alerts': 'ಸ್ಟಾಕ್ ಎಚ್ಚರಿಕೆಗಳು',
    'sidebar.barcode_qr': 'ಬಾರ್‌ಕೋಡ್ / QR',
    'sidebar.warehouse_transfer': 'ಗೋದಾಮು ವರ್ಗಾವಣೆ',
    'sidebar.batch_lot': 'ಬ್ಯಾಚ್ / ಲಾಟ್',
    'sidebar.kits_bundles': 'ಕಿಟ್‌ಗಳು & ಬಂಡಲ್‌ಗಳು',
    'sidebar.products_sku_master': 'ಉತ್ಪನ್ನಗಳು (SKU ಮಾಸ್ಟರ್)',
    'sidebar.overview': 'ಅವಲೋಕನ',
    'sidebar.zones': 'ವಲಯಗಳು',
    'sidebar.racks_bins': 'ರ್ಯಾಕ್‌ಗಳು & ಬಿನ್‌ಗಳು',
    'sidebar.allocations': 'ಹಂಚಿಕೆಗಳು',
    'sidebar.task_board': 'ಕಾರ್ಯ ಬೋರ್ಡ್',
    'sidebar.dock_schedule': 'ಡಾಕ್ ವೇಳಾಪಟ್ಟಿ',
    'sidebar.overstay_mgmt': 'ಓವರ್‌ಸ್ಟೇ ನಿರ್ವಹಣೆ',
    'sidebar.inbound_outbound': 'ಒಳಬರುವ/ಹೊರಹೋಗುವ',
    'sidebar.packaging': 'ಪ್ಯಾಕೇಜಿಂಗ್',
    'sidebar.workforce': 'ಕಾರ್ಯಪಡೆ',
    'sidebar.connectors': 'ಕನೆಕ್ಟರ್‌ಗಳು',
    'sidebar.order_sync': 'ಆದೇಶ ಸಿಂಕ್',
    'sidebar.fulfillment_center': 'ಪೂರೈಕೆ ಕೇಂದ್ರ',
    'sidebar.hsn_sac_codes': 'HSN / SAC ಕೋಡ್‌ಗಳು',
    'sidebar.gst_filing': 'GST ಸಲ್ಲಿಕೆ',
    'sidebar.invoices': 'ಇನ್‌ವಾಯಿಸ್‌ಗಳು',
    'sidebar.payments': 'ಪಾವತಿಗಳು',
    'sidebar.rate_cards': 'ದರ ಕಾರ್ಡ್‌ಗಳು',
    'sidebar.payouts': 'ಪಾವತಿಗಳು',
    'sidebar.host_settlements': 'ಹೋಸ್ಟ್ ಇತ್ಯರ್ಥಗಳು',
    'sidebar.my_bank_accounts': 'ನನ್ನ ಬ್ಯಾಂಕ್ ಖಾತೆಗಳು',
    'sidebar.reversals_claw_back': 'ಹಿಮ್ಮೆಟ್ಟು (ಕ್ಲಾ-ಬ್ಯಾಕ್)',
    'sidebar.auto_debit_enach': 'ಸ್ವಯಂ-ಡೆಬಿಟ್ (eNACH)',
    'sidebar.compliance_calendar': 'ಅನುಸರಣೆ ಕ್ಯಾಲೆಂಡರ್',
    'sidebar.gstr_1_report': 'GSTR-1 ವರದಿ',
    'sidebar.gstr_3b_summary': 'GSTR-3B ಸಾರಾಂಶ',
    'sidebar.gstr_9_annual': 'GSTR-9 ವಾರ್ಷಿಕ',
    'sidebar.vendors': 'ಮಾರಾಟಗಾರರು',
    'sidebar.purchase_orders': 'ಖರೀದಿ ಆದೇಶಗಳು',
    'sidebar.purchase_invoices': 'ಖರೀದಿ ಇನ್‌ವಾಯಿಸ್‌ಗಳು',
    'sidebar.gstr_2b_reconciliation': 'GSTR-2B ಸಮನ್ವಯ',
    'sidebar.tax_ledger': 'ತೆರಿಗೆ ಲೆಡ್ಜರ್',
    'sidebar.e_way_bills': 'ಇ-ವೇ ಬಿಲ್‌ಗಳು',
    'sidebar.credit_limits': 'ಕ್ರೆಡಿಟ್ ಮಿತಿಗಳು',
    'sidebar.receivables_ar': 'ಸ್ವೀಕರಿಸುವವು (AR)',
    'sidebar.payables_ap': 'ಪಾವತಿಸಬೇಕಾದವು (AP)',
    'sidebar.settlement_engine': 'ಇತ್ಯರ್ಥ ಎಂಜಿನ್',
    'sidebar.settlement_statement': 'ಇತ್ಯರ್ಥ ಹೇಳಿಕೆ',
    'sidebar.settlement_settings': 'ಇತ್ಯರ್ಥ ಸೆಟ್ಟಿಂಗ್‌ಗಳು',
    'sidebar.clawbacks': 'ಕ್ಲಾಬ್ಯಾಕ್‌ಗಳು',
    'sidebar.rolling_reserve': 'ರೋಲಿಂಗ್ ರಿಸರ್ವ್',
    'sidebar.gstr_8_tcs': 'GSTR-8 (TCS)',
    'sidebar.escrow': 'ಎಸ್ಕ್ರೊ',
    'sidebar.ledger': 'ಲೆಡ್ಜರ್',
    'sidebar.bank_recon': 'ಬ್ಯಾಂಕ್ ಸಮನ್ವಯ',
    'sidebar.tds_tracking': 'TDS ಟ್ರ್ಯಾಕಿಂಗ್',
    'sidebar.inbox': 'ಇನ್‌ಬಾಕ್ಸ್',
    'sidebar.preferences': 'ಆದ್ಯತೆಗಳು',
    'sidebar.templates': 'ಟೆಂಪ್ಲೇಟ್‌ಗಳು',
    'sidebar.all_reviews': 'ಎಲ್ಲಾ ವಿಮರ್ಶೆಗಳು',
    'sidebar.trust_badges': 'ನಂಬಿಕೆ & ಬ್ಯಾಡ್ಜ್‌ಗಳು',
    'sidebar.all_disputes': 'ಎಲ್ಲಾ ವಿವಾದಗಳು',
    'sidebar.sla_board': 'SLA ಬೋರ್ಡ್',
    'sidebar.shipment_lifecycle': 'ಶಿಪ್‌ಮೆಂಟ್ ಜೀವನಚಕ್ರ',
    'sidebar.partner_onboarding': 'ಪಾಲುದಾರ ಆನ್‌ಬೋರ್ಡಿಂಗ್',
    'sidebar.partner_management': 'ಪಾಲುದಾರ ನಿರ್ವಹಣೆ',
    'sidebar.freight_requests': 'ಸರಕು ವಿನಂತಿಗಳು',
    'sidebar.shipments': 'ಶಿಪ್‌ಮೆಂಟ್‌ಗಳು',
    'sidebar.earnings': 'ಗಳಿಕೆ',
    'sidebar.all_customers': 'ಎಲ್ಲಾ ಗ್ರಾಹಕರು',
    'sidebar.business_intelligence': 'ವ್ಯಾಪಾರ ಬುದ್ಧಿಮತ್ತೆ',
    'sidebar.financial': 'ಹಣಕಾಸು',
    'sidebar.platform_health': 'ಪ್ಲಾಟ್‌ಫಾರ್ಮ್ ಆರೋಗ್ಯ',
    'sidebar.abc_xyz_analysis': 'ABC/XYZ ವಿಶ್ಲೇಷಣೆ',
    'sidebar.reports_builder': 'ವರದಿ ಬಿಲ್ಡರ್',
    'sidebar.ondc_network': 'ONDC ನೆಟ್‌ವರ್ಕ್',
    'sidebar.api_gateway': 'API ಗೇಟ್‌ವೇ',
    'sidebar.settings': 'ಸೆಟ್ಟಿಂಗ್‌ಗಳು',
    'sidebar.master_data': 'ಮಾಸ್ಟರ್ ಡೇಟಾ',
    'sidebar.users_roles': 'ಬಳಕೆದಾರರು & ಪಾತ್ರಗಳು',
    'sidebar.kyc_queue': 'KYC ಸರತಿ',
    'sidebar.moderation': 'ಮಾಡರೇಷನ್',
    'sidebar.pricing_governance': 'ಬೆಲೆ ಆಡಳಿತ',
    'sidebar.platform_config': 'ಪ್ಲಾಟ್‌ಫಾರ್ಮ್ ಕಾನ್ಫಿಗ್',
    'sidebar.branding': 'ಬ್ರಾಂಡಿಂಗ್',
    'sidebar.business_rules': 'ವ್ಯಾಪಾರ ನಿಯಮಗಳು',
    'sidebar.doc_templates': 'ಡಾಕ್ ಟೆಂಪ್ಲೇಟ್‌ಗಳು',
    'sidebar.contract_clauses': 'ಒಪ್ಪಂದ ಷರತ್ತುಗಳು',
    'sidebar.contract_defaults': 'ಒಪ್ಪಂದ ಡೀಫಾಲ್ಟ್‌ಗಳು',
    'sidebar.contract_templates': 'ಒಪ್ಪಂದ ಟೆಂಪ್ಲೇಟ್‌ಗಳು',
    'sidebar.e_way_bill_templates': 'ಇ-ವೇ ಬಿಲ್ ಟೆಂಪ್ಲೇಟ್',
    'sidebar.template_review_queue': 'ಟೆಂಪ್ಲೇಟ್ ವಿಮರ್ಶೆ ಸರತಿ',
    'sidebar.team_members': 'ತಂಡ ಸದಸ್ಯರು',
    'sidebar.landing_page': 'ಲ್ಯಾಂಡಿಂಗ್ ಪುಟ',
    'sidebar.listing_fields': 'ಪಟ್ಟಿ ಕ್ಷೇತ್ರಗಳು',
    'sidebar.dpdp_compliance': 'DPDP ಅನುಸರಣೆ',
    'sidebar.help_faq': 'ಸಹಾಯ & FAQ',
    'sidebar.audit_logs': 'ಆಡಿಟ್ ಲಾಗ್‌ಗಳು',
    'sidebar.sla_management': 'SLA ನಿರ್ವಹಣೆ',
    'sidebar.feature_flags': 'ಫೀಚರ್ ಫ್ಲ್ಯಾಗ್‌ಗಳು',
    'sidebar.roles_permissions': 'ಪಾತ್ರಗಳು & ಅನುಮತಿಗಳು',
    'sidebar.webhooks_apis': 'ವೆಬ್‌ಹುಕ್ಸ್ & API',
    'sidebar.notifications_mgmt': 'ಅಧಿಸೂಚನೆ ನಿರ್ವಹಣೆ',
    'sidebar.workflow_engine': 'ವರ್ಕ್‌ಫ್ಲೋ ಎಂಜಿನ್',
    'sidebar.compliance_tools': 'ಅನುಸರಣೆ ಉಪಕರಣಗಳು',
    'sidebar.scheduled_reports': 'ನಿಗದಿತ ವರದಿಗಳು',
    'sidebar.anomaly_alerts': 'ವೈಪರೀತ್ಯ ಎಚ್ಚರಿಕೆಗಳು',
    'sidebar.support_tickets': 'ಬೆಂಬಲ ಟಿಕೆಟ್‌ಗಳು',
    'sidebar.data_import_export': 'ಡೇಟಾ ಆಮದು/ರಫ್ತು',
    'sidebar.sms_whatsapp': 'SMS & WhatsApp',
    'sidebar.payment_gateway': 'ಪಾವತಿ ಗೇಟ್‌ವೇ',
    'sidebar.api_documentation': 'API ದಾಖಲೀಕರಣ',
    'sidebar.changelog': 'ಬದಲಾವಣೆ ಲಾಗ್',
    'sidebar.my_profile': 'ನನ್ನ ಪ್ರೊಫೈಲ್',
    'sidebar.theme_appearance': 'ಥೀಮ್ & ನೋಟ',
    'sidebar.help_center': 'ಸಹಾಯ ಕೇಂದ್ರ',
  },

  // ----- Marathi -----
  MR: {
    'sidebar.main': 'मुख्य',
    'sidebar.my_business': 'माझा व्यवसाय',
    'sidebar.management': 'व्यवस्थापन',
    'sidebar.operations': 'ऑपरेशन्स',
    'sidebar.e_commerce': 'ई-कॉमर्स',
    'sidebar.compliance': 'अनुपालन',
    'sidebar.finance': 'वित्त',
    'sidebar.finance_settlements': 'वित्त व निकाल',
    'sidebar.communication': 'संवाद',
    'sidebar.administration': 'प्रशासन',
    'sidebar.analytics': 'विश्लेषण',
    'sidebar.integrations': 'एकत्रीकरण',
    'sidebar.system': 'प्रणाली',
    'sidebar.dashboard': 'डॅशबोर्ड',
    'sidebar.my_storage': 'माझी साठवण',
    'sidebar.my_organization': 'माझी संस्था',
    'sidebar.my_team': 'माझा संघ',
    'sidebar.my_warehouses': 'माझी गोदामे',
    'sidebar.my_billing': 'माझे बिलिंग',
    'sidebar.invoice_settings': 'चलन सेटिंग्ज',
    'sidebar.warehouses': 'गोदामे',
    'sidebar.marketplace': 'बाजारपेठ',
    'sidebar.bookings': 'बुकिंग',
    'sidebar.contracts': 'करार',
    'sidebar.inventory': 'इन्व्हेंटरी',
    'sidebar.procurement': 'खरेदी',
    'sidebar.space_mgmt': 'जागा व्यवस्थापन',
    'sidebar.billing': 'बिलिंग',
    'sidebar.settlements': 'निकाल',
    'sidebar.notifications': 'सूचना',
    'sidebar.reviews_trust': 'पुनरावलोकने व विश्वास',
    'sidebar.disputes': 'विवाद',
    'sidebar.logistics': 'लॉजिस्टिक्स',
    'sidebar.customers': 'ग्राहक',
    'sidebar.gst_compliance': 'GST व अनुपालन',
    'sidebar.my_settings': 'माझ्या सेटिंग्ज',
    'sidebar.all_listings': 'सर्व सूची',
    'sidebar.onboard_new': 'नवीन जोडा',
    'sidebar.configure_layout': 'लेआउट कॉन्फिगर',
    'sidebar.listing_approvals': 'सूची मंजुरी',
    'sidebar.map_view': 'नकाशा दृश्य',
    'sidebar.search': 'शोधा',
    'sidebar.saved_watchlist': 'जतन केलेले व वॉचलिस्ट',
    'sidebar.all_bookings': 'सर्व बुकिंग',
    'sidebar.booking_approvals': 'बुकिंग मंजुरी',
    'sidebar.all_contracts': 'सर्व करार',
    'sidebar.warehouse_layout': 'गोदाम लेआउट',
    'sidebar.stock': 'साठा',
    'sidebar.grn_inbound': 'GRN (आवक)',
    'sidebar.putaway_workbench': 'पुटअवे वर्कबेंच',
    'sidebar.dispatch_outbound': 'रवानगी (जावक)',
    'sidebar.returns': 'परतावे',
    'sidebar.damage_register': 'नुकसान नोंद',
    'sidebar.damage_approvals': 'नुकसान मंजुरी',
    'sidebar.cycle_count': 'चक्र गणना',
    'sidebar.stock_alerts': 'साठा सूचना',
    'sidebar.barcode_qr': 'बारकोड / QR',
    'sidebar.warehouse_transfer': 'गोदाम हस्तांतरण',
    'sidebar.batch_lot': 'बॅच / लॉट',
    'sidebar.kits_bundles': 'किट व बंडल',
    'sidebar.products_sku_master': 'उत्पादने (SKU मास्टर)',
    'sidebar.overview': 'आढावा',
    'sidebar.zones': 'क्षेत्र',
    'sidebar.racks_bins': 'रॅक व बिन',
    'sidebar.allocations': 'वाटप',
    'sidebar.task_board': 'कार्य बोर्ड',
    'sidebar.dock_schedule': 'डॉक वेळापत्रक',
    'sidebar.overstay_mgmt': 'अतिमुक्काम व्यवस्थापन',
    'sidebar.inbound_outbound': 'आवक/जावक',
    'sidebar.packaging': 'पॅकेजिंग',
    'sidebar.workforce': 'कामगार',
    'sidebar.connectors': 'कनेक्टर',
    'sidebar.order_sync': 'ऑर्डर सिंक',
    'sidebar.fulfillment_center': 'पूर्तता केंद्र',
    'sidebar.hsn_sac_codes': 'HSN / SAC कोड',
    'sidebar.gst_filing': 'GST भरणे',
    'sidebar.invoices': 'चलन',
    'sidebar.payments': 'देयके',
    'sidebar.rate_cards': 'दर कार्ड',
    'sidebar.payouts': 'पेआउट',
    'sidebar.host_settlements': 'होस्ट निकाल',
    'sidebar.my_bank_accounts': 'माझी बँक खाती',
    'sidebar.reversals_claw_back': 'उलटफेर (क्लॉ-बॅक)',
    'sidebar.auto_debit_enach': 'स्वयं-डेबिट (eNACH)',
    'sidebar.compliance_calendar': 'अनुपालन कॅलेंडर',
    'sidebar.gstr_1_report': 'GSTR-1 अहवाल',
    'sidebar.gstr_3b_summary': 'GSTR-3B सारांश',
    'sidebar.gstr_9_annual': 'GSTR-9 वार्षिक',
    'sidebar.vendors': 'विक्रेते',
    'sidebar.purchase_orders': 'खरेदी आदेश',
    'sidebar.purchase_invoices': 'खरेदी चलन',
    'sidebar.gstr_2b_reconciliation': 'GSTR-2B मेळ',
    'sidebar.tax_ledger': 'कर खतावणी',
    'sidebar.e_way_bills': 'ई-वे बिल',
    'sidebar.credit_limits': 'क्रेडिट मर्यादा',
    'sidebar.receivables_ar': 'येणे (AR)',
    'sidebar.payables_ap': 'देणे (AP)',
    'sidebar.settlement_engine': 'निकाल इंजिन',
    'sidebar.settlement_statement': 'निकाल विवरण',
    'sidebar.settlement_settings': 'निकाल सेटिंग्ज',
    'sidebar.clawbacks': 'क्लॉबॅक',
    'sidebar.rolling_reserve': 'रोलिंग रिझर्व्ह',
    'sidebar.gstr_8_tcs': 'GSTR-8 (TCS)',
    'sidebar.escrow': 'एस्क्रो',
    'sidebar.ledger': 'खतावणी',
    'sidebar.bank_recon': 'बँक मेळ',
    'sidebar.tds_tracking': 'TDS ट्रॅकिंग',
    'sidebar.inbox': 'इनबॉक्स',
    'sidebar.preferences': 'प्राधान्ये',
    'sidebar.templates': 'टेम्प्लेट',
    'sidebar.all_reviews': 'सर्व पुनरावलोकने',
    'sidebar.trust_badges': 'विश्वास व बॅज',
    'sidebar.all_disputes': 'सर्व विवाद',
    'sidebar.sla_board': 'SLA बोर्ड',
    'sidebar.shipment_lifecycle': 'शिपमेंट जीवनचक्र',
    'sidebar.partner_onboarding': 'भागीदार ऑनबोर्डिंग',
    'sidebar.partner_management': 'भागीदार व्यवस्थापन',
    'sidebar.freight_requests': 'माल विनंत्या',
    'sidebar.shipments': 'शिपमेंट',
    'sidebar.earnings': 'कमाई',
    'sidebar.all_customers': 'सर्व ग्राहक',
    'sidebar.business_intelligence': 'व्यवसाय बुद्धिमत्ता',
    'sidebar.financial': 'आर्थिक',
    'sidebar.platform_health': 'प्लॅटफॉर्म आरोग्य',
    'sidebar.abc_xyz_analysis': 'ABC/XYZ विश्लेषण',
    'sidebar.reports_builder': 'अहवाल निर्माता',
    'sidebar.ondc_network': 'ONDC नेटवर्क',
    'sidebar.api_gateway': 'API गेटवे',
    'sidebar.settings': 'सेटिंग्ज',
    'sidebar.master_data': 'मास्टर डेटा',
    'sidebar.users_roles': 'वापरकर्ते व भूमिका',
    'sidebar.kyc_queue': 'KYC रांग',
    'sidebar.moderation': 'नियंत्रण',
    'sidebar.pricing_governance': 'किंमत प्रशासन',
    'sidebar.platform_config': 'प्लॅटफॉर्म कॉन्फिग',
    'sidebar.branding': 'ब्रँडिंग',
    'sidebar.business_rules': 'व्यावसायिक नियम',
    'sidebar.doc_templates': 'दस्तऐवज टेम्प्लेट',
    'sidebar.contract_clauses': 'करार कलमे',
    'sidebar.contract_defaults': 'करार डीफॉल्ट',
    'sidebar.contract_templates': 'करार टेम्प्लेट',
    'sidebar.e_way_bill_templates': 'ई-वे बिल टेम्प्लेट',
    'sidebar.template_review_queue': 'टेम्प्लेट पुनरावलोकन',
    'sidebar.team_members': 'संघ सदस्य',
    'sidebar.landing_page': 'लँडिंग पृष्ठ',
    'sidebar.listing_fields': 'सूची फील्ड',
    'sidebar.dpdp_compliance': 'DPDP अनुपालन',
    'sidebar.help_faq': 'मदत व FAQ',
    'sidebar.audit_logs': 'ऑडिट लॉग',
    'sidebar.sla_management': 'SLA व्यवस्थापन',
    'sidebar.feature_flags': 'फीचर फ्लॅग',
    'sidebar.roles_permissions': 'भूमिका व परवानग्या',
    'sidebar.webhooks_apis': 'वेबहुक व API',
    'sidebar.notifications_mgmt': 'सूचना व्यवस्थापन',
    'sidebar.workflow_engine': 'वर्कफ्लो इंजिन',
    'sidebar.compliance_tools': 'अनुपालन साधने',
    'sidebar.scheduled_reports': 'नियोजित अहवाल',
    'sidebar.anomaly_alerts': 'विसंगती सूचना',
    'sidebar.support_tickets': 'समर्थन तिकीट',
    'sidebar.data_import_export': 'डेटा आयात/निर्यात',
    'sidebar.sms_whatsapp': 'SMS व WhatsApp',
    'sidebar.payment_gateway': 'पेमेंट गेटवे',
    'sidebar.api_documentation': 'API दस्तऐवजीकरण',
    'sidebar.changelog': 'बदल लॉग',
    'sidebar.my_profile': 'माझी प्रोफाइल',
    'sidebar.theme_appearance': 'थीम व स्वरूप',
    'sidebar.help_center': 'मदत केंद्र',
  },

  // ----- Bengali -----
  BN: {
    'sidebar.main': 'প্রধান',
    'sidebar.my_business': 'আমার ব্যবসা',
    'sidebar.management': 'পরিচালনা',
    'sidebar.operations': 'কার্যক্রম',
    'sidebar.e_commerce': 'ই-কমার্স',
    'sidebar.compliance': 'সম্মতি',
    'sidebar.finance': 'অর্থ',
    'sidebar.finance_settlements': 'অর্থ ও নিষ্পত্তি',
    'sidebar.communication': 'যোগাযোগ',
    'sidebar.administration': 'প্রশাসন',
    'sidebar.analytics': 'বিশ্লেষণ',
    'sidebar.integrations': 'সংহতিকরণ',
    'sidebar.system': 'সিস্টেম',
    'sidebar.dashboard': 'ড্যাশবোর্ড',
    'sidebar.my_storage': 'আমার স্টোরেজ',
    'sidebar.my_organization': 'আমার সংগঠন',
    'sidebar.my_team': 'আমার দল',
    'sidebar.my_warehouses': 'আমার গুদাম',
    'sidebar.my_billing': 'আমার বিলিং',
    'sidebar.invoice_settings': 'চালান সেটিংস',
    'sidebar.warehouses': 'গুদাম',
    'sidebar.marketplace': 'বাজার',
    'sidebar.bookings': 'বুকিং',
    'sidebar.contracts': 'চুক্তি',
    'sidebar.inventory': 'ইনভেন্টরি',
    'sidebar.procurement': 'ক্রয়',
    'sidebar.space_mgmt': 'স্থান ব্যবস্থাপনা',
    'sidebar.billing': 'বিলিং',
    'sidebar.settlements': 'নিষ্পত্তি',
    'sidebar.notifications': 'নোটিফিকেশন',
    'sidebar.reviews_trust': 'পর্যালোচনা ও বিশ্বাস',
    'sidebar.disputes': 'বিরোধ',
    'sidebar.logistics': 'লজিস্টিকস',
    'sidebar.customers': 'গ্রাহক',
    'sidebar.gst_compliance': 'GST ও সম্মতি',
    'sidebar.my_settings': 'আমার সেটিংস',
    'sidebar.all_listings': 'সমস্ত তালিকা',
    'sidebar.onboard_new': 'নতুন যোগ করুন',
    'sidebar.configure_layout': 'লেআউট কনফিগার',
    'sidebar.listing_approvals': 'তালিকা অনুমোদন',
    'sidebar.map_view': 'মানচিত্র দৃশ্য',
    'sidebar.search': 'অনুসন্ধান',
    'sidebar.saved_watchlist': 'সংরক্ষিত ও ওয়াচলিস্ট',
    'sidebar.all_bookings': 'সমস্ত বুকিং',
    'sidebar.booking_approvals': 'বুকিং অনুমোদন',
    'sidebar.all_contracts': 'সমস্ত চুক্তি',
    'sidebar.warehouse_layout': 'গুদাম লেআউট',
    'sidebar.stock': 'স্টক',
    'sidebar.grn_inbound': 'GRN (অভ্যন্তরীণ)',
    'sidebar.putaway_workbench': 'পুটঅ্যাওয়ে ওয়ার্কবেঞ্চ',
    'sidebar.dispatch_outbound': 'প্রেরণ (বহির্গামী)',
    'sidebar.returns': 'রিটার্ন',
    'sidebar.damage_register': 'ক্ষতি নিবন্ধ',
    'sidebar.damage_approvals': 'ক্ষতি অনুমোদন',
    'sidebar.cycle_count': 'চক্র গণনা',
    'sidebar.stock_alerts': 'স্টক সতর্কতা',
    'sidebar.barcode_qr': 'বারকোড / QR',
    'sidebar.warehouse_transfer': 'গুদাম স্থানান্তর',
    'sidebar.batch_lot': 'ব্যাচ / লট',
    'sidebar.kits_bundles': 'কিট ও বান্ডেল',
    'sidebar.products_sku_master': 'পণ্য (SKU মাস্টার)',
    'sidebar.overview': 'সংক্ষিপ্ত বিবরণ',
    'sidebar.zones': 'জোন',
    'sidebar.racks_bins': 'র‍্যাক ও বিন',
    'sidebar.allocations': 'বরাদ্দ',
    'sidebar.task_board': 'কাজের বোর্ড',
    'sidebar.dock_schedule': 'ডক সময়সূচী',
    'sidebar.overstay_mgmt': 'অতিবাস ব্যবস্থাপনা',
    'sidebar.inbound_outbound': 'অভ্যন্তরীণ/বহির্গামী',
    'sidebar.packaging': 'প্যাকেজিং',
    'sidebar.workforce': 'কর্মীবাহিনী',
    'sidebar.connectors': 'কানেক্টর',
    'sidebar.order_sync': 'অর্ডার সিঙ্ক',
    'sidebar.fulfillment_center': 'পূরণ কেন্দ্র',
    'sidebar.hsn_sac_codes': 'HSN / SAC কোড',
    'sidebar.gst_filing': 'GST ফাইলিং',
    'sidebar.invoices': 'চালান',
    'sidebar.payments': 'পেমেন্ট',
    'sidebar.rate_cards': 'রেট কার্ড',
    'sidebar.payouts': 'পেআউট',
    'sidebar.host_settlements': 'হোস্ট নিষ্পত্তি',
    'sidebar.my_bank_accounts': 'আমার ব্যাঙ্ক অ্যাকাউন্ট',
    'sidebar.reversals_claw_back': 'উল্টানো (ক্ল-ব্যাক)',
    'sidebar.auto_debit_enach': 'অটো-ডেবিট (eNACH)',
    'sidebar.compliance_calendar': 'সম্মতি ক্যালেন্ডার',
    'sidebar.gstr_1_report': 'GSTR-1 প্রতিবেদন',
    'sidebar.gstr_3b_summary': 'GSTR-3B সারাংশ',
    'sidebar.gstr_9_annual': 'GSTR-9 বার্ষিক',
    'sidebar.vendors': 'বিক্রেতা',
    'sidebar.purchase_orders': 'ক্রয় আদেশ',
    'sidebar.purchase_invoices': 'ক্রয় চালান',
    'sidebar.gstr_2b_reconciliation': 'GSTR-2B মিলকরণ',
    'sidebar.tax_ledger': 'কর খতিয়ান',
    'sidebar.e_way_bills': 'ই-ওয়ে বিল',
    'sidebar.credit_limits': 'ক্রেডিট সীমা',
    'sidebar.receivables_ar': 'প্রাপ্য (AR)',
    'sidebar.payables_ap': 'প্রদেয় (AP)',
    'sidebar.settlement_engine': 'নিষ্পত্তি ইঞ্জিন',
    'sidebar.settlement_statement': 'নিষ্পত্তি বিবৃতি',
    'sidebar.settlement_settings': 'নিষ্পত্তি সেটিংস',
    'sidebar.clawbacks': 'ক্লব্যাক',
    'sidebar.rolling_reserve': 'রোলিং রিজার্ভ',
    'sidebar.gstr_8_tcs': 'GSTR-8 (TCS)',
    'sidebar.escrow': 'এসক্রো',
    'sidebar.ledger': 'খতিয়ান',
    'sidebar.bank_recon': 'ব্যাঙ্ক মিলকরণ',
    'sidebar.tds_tracking': 'TDS ট্র্যাকিং',
    'sidebar.inbox': 'ইনবক্স',
    'sidebar.preferences': 'পছন্দসমূহ',
    'sidebar.templates': 'টেমপ্লেট',
    'sidebar.all_reviews': 'সমস্ত পর্যালোচনা',
    'sidebar.trust_badges': 'বিশ্বাস ও ব্যাজ',
    'sidebar.all_disputes': 'সমস্ত বিরোধ',
    'sidebar.sla_board': 'SLA বোর্ড',
    'sidebar.shipment_lifecycle': 'শিপমেন্ট জীবনচক্র',
    'sidebar.partner_onboarding': 'অংশীদার অনবোর্ডিং',
    'sidebar.partner_management': 'অংশীদার ব্যবস্থাপনা',
    'sidebar.freight_requests': 'মাল অনুরোধ',
    'sidebar.shipments': 'শিপমেন্ট',
    'sidebar.earnings': 'আয়',
    'sidebar.all_customers': 'সমস্ত গ্রাহক',
    'sidebar.business_intelligence': 'ব্যবসায়িক বুদ্ধিমত্তা',
    'sidebar.financial': 'আর্থিক',
    'sidebar.platform_health': 'প্ল্যাটফর্ম স্বাস্থ্য',
    'sidebar.abc_xyz_analysis': 'ABC/XYZ বিশ্লেষণ',
    'sidebar.reports_builder': 'প্রতিবেদন নির্মাতা',
    'sidebar.ondc_network': 'ONDC নেটওয়ার্ক',
    'sidebar.api_gateway': 'API গেটওয়ে',
    'sidebar.settings': 'সেটিংস',
    'sidebar.master_data': 'মাস্টার ডেটা',
    'sidebar.users_roles': 'ব্যবহারকারী ও ভূমিকা',
    'sidebar.kyc_queue': 'KYC সারি',
    'sidebar.moderation': 'পরিমিতিকরণ',
    'sidebar.pricing_governance': 'মূল্য প্রশাসন',
    'sidebar.platform_config': 'প্ল্যাটফর্ম কনফিগ',
    'sidebar.branding': 'ব্র্যান্ডিং',
    'sidebar.business_rules': 'ব্যবসায়িক নিয়ম',
    'sidebar.doc_templates': 'নথি টেমপ্লেট',
    'sidebar.contract_clauses': 'চুক্তি ধারা',
    'sidebar.contract_defaults': 'চুক্তি ডিফল্ট',
    'sidebar.contract_templates': 'চুক্তি টেমপ্লেট',
    'sidebar.e_way_bill_templates': 'ই-ওয়ে বিল টেমপ্লেট',
    'sidebar.template_review_queue': 'টেমপ্লেট পর্যালোচনা সারি',
    'sidebar.team_members': 'দল সদস্য',
    'sidebar.landing_page': 'ল্যান্ডিং পৃষ্ঠা',
    'sidebar.listing_fields': 'তালিকা ক্ষেত্র',
    'sidebar.dpdp_compliance': 'DPDP সম্মতি',
    'sidebar.help_faq': 'সাহায্য ও FAQ',
    'sidebar.audit_logs': 'অডিট লগ',
    'sidebar.sla_management': 'SLA ব্যবস্থাপনা',
    'sidebar.feature_flags': 'ফিচার ফ্ল্যাগ',
    'sidebar.roles_permissions': 'ভূমিকা ও অনুমতি',
    'sidebar.webhooks_apis': 'ওয়েবহুক ও API',
    'sidebar.notifications_mgmt': 'নোটিফিকেশন ব্যবস্থাপনা',
    'sidebar.workflow_engine': 'ওয়ার্কফ্লো ইঞ্জিন',
    'sidebar.compliance_tools': 'সম্মতি সরঞ্জাম',
    'sidebar.scheduled_reports': 'নির্ধারিত প্রতিবেদন',
    'sidebar.anomaly_alerts': 'অসংগতি সতর্কতা',
    'sidebar.support_tickets': 'সহায়তা টিকিট',
    'sidebar.data_import_export': 'ডেটা আমদানি/রপ্তানি',
    'sidebar.sms_whatsapp': 'SMS ও WhatsApp',
    'sidebar.payment_gateway': 'পেমেন্ট গেটওয়ে',
    'sidebar.api_documentation': 'API নথিপত্র',
    'sidebar.changelog': 'পরিবর্তন লগ',
    'sidebar.my_profile': 'আমার প্রোফাইল',
    'sidebar.theme_appearance': 'থিম ও চেহারা',
    'sidebar.help_center': 'সাহায্য কেন্দ্র',
  },
};

export default overlays;
